import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, map, of, switchMap } from 'rxjs';

import { NumpadDisplayService } from '../../../../lib/numpad/numpad-display.service';
import { BetslipType } from '../../../core/betslip-type';
import BetbuilderActions from '../../bet-builder/actions';
import { getComboSlipId } from '../../bet-generation/services/slip.utils';
import { FreeBetToken } from '../../reward-tokens/reward-tokens.model';
import { selectFreeBetTokenFactory } from '../../reward-tokens/selectors';
import { BetslipStakeActions } from '../../stake/actions';
import { BETSLIP_LINEAR_NUMPAD, IStake } from '../../stake/models';
import { ILinearContainerStakeService } from '../../stake/services/digital/linear/linear-container-stake.interface';
import { IStakeErrorViewModel } from '../../summary/models/stake-errors.model';
import { LinearStakeErrorsService } from '../../summary/services/linear/linear-stake-errors.service';
import { LinearBetBuilderPickId } from '../models';
import { selectBetBuilderPickStakeFactory } from '../selectors';

@Injectable()
export class BetBuilderContainerStakeService implements ILinearContainerStakeService {
    private readonly stake$: Observable<IStake | null>;
    private readonly stakeError$: Observable<IStakeErrorViewModel | null>;
    private readonly pickId$: BehaviorSubject<LinearBetBuilderPickId | null>;
    private readonly adornmentText$: Observable<string | undefined>;

    private pickId?: LinearBetBuilderPickId;

    constructor(
        private store: Store,
        private stakeErrorService: LinearStakeErrorsService,
        private numpadDisplayService: NumpadDisplayService,
    ) {
        this.pickId$ = new BehaviorSubject<LinearBetBuilderPickId | null>(null);

        this.adornmentText$ = of(undefined);

        this.stake$ = this.pickId$.pipe(switchMap((pickId) => (pickId ? this.store.select(selectBetBuilderPickStakeFactory(pickId)) : of(null))));
        const errors$ = this.pickId$.pipe(switchMap((pickId) => (pickId ? this.stakeErrorService.getStakeErrors$(getComboSlipId()) : of(null))));

        this.stakeError$ = combineLatest([errors$, this.stake$]).pipe(
            map(([errors, stake]) => {
                if (!errors) {
                    return null;
                }

                const isNumpadOpen = this.numpadDisplayService.isOpen(this.getKeypadId());

                return this.stakeErrorService.convertToViewModel(errors, stake, isNumpadOpen);
            }),
        );
    }

    updatePickId(pickId: LinearBetBuilderPickId) {
        this.pickId = pickId;
        this.pickId$.next(pickId);
    }

    getAdornmentText(): Observable<string | undefined> {
        return this.adornmentText$;
    }

    getKeypadId(): string {
        return BETSLIP_LINEAR_NUMPAD;
    }

    setStake(stakeStr: string, stake: number | null): void {
        if (!this.pickId) {
            return;
        }

        this.store.dispatch(BetbuilderActions.setPickStake({ pickId: this.pickId.toString(), stake, stakeStr, silent: false }));
    }

    clickStake(stake: IStake | null): void {
        const stakeAmount = stake?.stake ?? null;

        this.store.dispatch(BetslipStakeActions.trackStakeClick({ stakeAmount, betslipType: BetslipType.BetBuilder }));
    }

    getStake$(): Observable<IStake | null> {
        return this.stake$;
    }

    getStakeErrors$(): Observable<IStakeErrorViewModel | null> {
        return this.stakeError$;
    }

    getFreeBetToken$(): Observable<FreeBetToken | null> {
        return this.pickId$.pipe(
            switchMap((pickId) =>
                pickId ? this.store.select(selectFreeBetTokenFactory({ betslipType: BetslipType.BetBuilder, pickId })) : of(null),
            ),
        );
    }
}
